export enum Dimension {
	Pixels = 'pixels',
	Inches = 'inches',
	Centimeters = 'centimeters',
	Millimeters = 'millimeters',
	Points = 'points',
	Picas = 'picas'
}

export const ScaleTable: Record<Dimension, Record<Dimension, number>> = {
	[Dimension.Pixels]: {
		[Dimension.Pixels]: 1,
		[Dimension.Inches]: 1,
		[Dimension.Centimeters]: 1,
		[Dimension.Millimeters]: 1,
		[Dimension.Points]: 1,
		[Dimension.Picas]: 1
	},
	[Dimension.Inches]: {
		[Dimension.Pixels]: 1,
		[Dimension.Inches]: 1,
		[Dimension.Centimeters]: 2.54,
		[Dimension.Millimeters]: 25.4,
		[Dimension.Points]: 72,
		[Dimension.Picas]: 6
	},
	[Dimension.Centimeters]: {
		[Dimension.Pixels]: 1,
		[Dimension.Inches]: 1 / 2.54,
		[Dimension.Centimeters]: 1,
		[Dimension.Millimeters]: 10,
		[Dimension.Points]: 72 / 2.54,
		[Dimension.Picas]: 72 / 30.48
	},
	[Dimension.Millimeters]: {
		[Dimension.Pixels]: 1,
		[Dimension.Inches]: 1 / 25.4,
		[Dimension.Centimeters]: 0.1,
		[Dimension.Millimeters]: 1,
		[Dimension.Points]: 72 / 25.4,
		[Dimension.Picas]: 72 / 304.8
	},
	[Dimension.Points]: {
		[Dimension.Pixels]: 1,
		[Dimension.Inches]: 1 / 72,
		[Dimension.Centimeters]: 2.54 / 72,
		[Dimension.Millimeters]: 25.4 / 72,
		[Dimension.Points]: 1,
		[Dimension.Picas]: 1 / 12
	},
	[Dimension.Picas]: {
		[Dimension.Pixels]: 1,
		[Dimension.Inches]: 1 / 6,
		[Dimension.Centimeters]: 2.54 / 6,
		[Dimension.Millimeters]: 25.4 / 6,
		[Dimension.Points]: 12,
		[Dimension.Picas]: 1
	}
};

export class Dimensions {
	/**
	 * Converts a width and height from one dimension to another.
	 * If the original unit is provided and the `from` value is `Dimension.Pixels`, it will convert to that first, then to the target unit.
	 * This is used for pixel-to-point conversions while maintaining the original, physical scale.
	 * @param valueW
	 * @param valueH
	 * @param from
	 * @param to
	 * @param originalScale
	 * @param originalUnit
	 */
	public static convert(
		valueW: number,
		valueH: number,
		from: Dimension,
		to: Dimension,
		originalScale: number = 300,
		originalUnit?: Dimension
	): { width: number; height: number; originalScale?: number } {
		if (from === to) {
			return { width: valueW, height: valueH, originalScale };
		}

		const output = {
			width: this.convertOne(valueW, from, to, originalScale, originalUnit),
			height: this.convertOne(valueH, from, to, originalScale, originalUnit)
		};

		if (to === Dimension.Pixels) {
			return { ...output, originalScale };
		}

		// console.log('originalScale', originalScale, this.getScalingFactor(from, to));

		// Adjust the original scale to match the new unit:
		// - While 50cm is exactly equal to 500mm, the 100 scale is not equivalent:
		// 	  - 50cm@100 becomes 5,000px.
		//    - 500mm@100 becomes 50,000px.
		//    - In reality, the 500mm conversion should end up at a scale of 10 DPMm, which will give the same 5,000px grid.
		let newScale = this.convertScale(originalScale, from, to);

		// see if we can convert DPx to DPy if coming from pixels
		if (from === Dimension.Pixels && originalUnit) {
			newScale = this.convertScale(newScale, originalUnit, to);
		}

		return {
			...output,
			originalScale: newScale
		};
	}

	/**
	 * Converts a single value from one dimension to another.
	 *
	 * `originalScale` semantics: by default it is interpreted as pixels-per-`from`-unit
	 * (e.g. for `from = Inches`, `300` means 300 px/in). When `originalUnit` is provided
	 * it is interpreted as pixels-per-`originalUnit` instead, and the conversion routes
	 * via `originalUnit`. This is required whenever `originalScale` is anchored to a
	 * different unit than `from` or `to`, e.g.:
	 *  - Pixels → Points with `originalUnit = Inches` (existing usage).
	 *  - Points → Pixels with `originalUnit = Inches`, where the standard typographic
	 *    convention is `pixels = points × (ppi / 72)`, not `points × ppi`.
	 *
	 * @param value
	 * @param from
	 * @param to
	 * @param originalScale
	 * @param originalUnit
	 * @param round
	 */
	public static convertOne(
		value: number,
		from: Dimension,
		to: Dimension,
		originalScale: number = 300,
		originalUnit?: Dimension,
		round: boolean = true
	) {
		if (from === to) {
			return value;
		}

		if (!originalScale) {
			originalScale = 300;
		}

		// Pixel-target detour, symmetric to the pixel-source detour below.
		// When `originalUnit` is provided, `originalScale` is interpreted as pixels-per-`originalUnit`.
		// Without this branch, `convertOne(9, Points, Pixels, 300, Inches)` would treat the scale as
		// pixels-per-point and return `9 × 300 = 2700`, instead of the correct typographic result
		// `9 × (300 / 72) = 37.5`.
		if (
			to === Dimension.Pixels &&
			from !== Dimension.Pixels &&
			originalUnit &&
			originalUnit !== from &&
			originalUnit !== to
		) {
			const inOriginalUnit = this.convertOne(value, from, originalUnit, originalScale, null, false);
			return this.convertOne(inOriginalUnit, originalUnit, to, originalScale, null, round);
		}

		const scalingFactor = this.getScalingFactor(from, to);
		let scaled = value;
		if (from !== Dimension.Pixels) {
			scaled = value * originalScale;
		} else if (originalUnit) {
			// Pixel-source detour: convert to `originalUnit` first so `originalScale` (px/originalUnit)
			// can be applied correctly, then convert from `originalUnit` to `to`.
			const original = this.convertOne(value, from, originalUnit, originalScale, null, false);
			const newScale = this.convertScale(originalScale, from, originalUnit);
			return this.convertOne(original, originalUnit, to, newScale);
		}

		if (to === Dimension.Pixels) {
			scaled *= scalingFactor;
			return round ? Math.round(scaled) : scaled;
		}

		return (scaled / originalScale) * scalingFactor;
	}

	/**
	 * Gets the scaling factor between two dimensions.
	 * The scaling factor is the number you would multiply a value in the `from` dimension by to get the equivalent value in the `to` dimension.
	 * For example, to convert from inches to centimeters, you would multiply by 2.54, so the scaling factor from inches to centimeters is 2.54.
	 * To convert from centimeters to inches, you would multiply by 1/2.54, so the scaling factor from centimeters to inches is 1/2.54.
	 * If there is no defined scaling factor between the two dimensions, it defaults to 1 (no conversion).
	 * @param from
	 * @param to
	 */
	public static getScalingFactor(from: Dimension, to: Dimension) {
		if (from === to) {
			return 1;
		}

		return ScaleTable[from]?.[to] ?? 1;
	}

	/**
	 * Converts a scale from one dimension to another.
	 * The scale is the number of pixels per unit of the original dimension. For example, if the original dimension is inches and the scale is 300, that means there are 300 pixels per inch.
	 * @param value
	 * @param from
	 * @param to
	 */
	public static convertScale(value: number, from: Dimension, to: Dimension) {
		if (from === to || value === undefined || value === null || !from || !to) {
			return value;
		}

		return value / this.getScalingFactor(from, to);
	}
}
